import Alert from '@amzn/meridian/alert';
import Loader from '@amzn/meridian/loader';
import React, { useContext } from 'react';
import { FormattedMessage } from 'react-intl';
import { StateContext } from '../../GlobalStateStore';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import Goals from './Goals';
import CreateGoalButton from './CreateGoalButton';
import { Profile } from '../../Types';
import { GoalPage } from './helper';
import useProxy from '../../hooks/useProxy';

interface WrapperProps {
  loading: boolean;
  loaded: boolean;
  error: boolean;
  displayedProfile: Profile;
  loggedInProfile: Profile;
  refreshGoalsFlag: boolean;
  setIsCreateGoalOpen?: Function;
  page: GoalPage;
}

const GoalSettingContainerWrapper: React.FC<WrapperProps> = ({
  loading,
  loaded,
  error,
  displayedProfile,
  loggedInProfile,
  refreshGoalsFlag,
  setIsCreateGoalOpen,
  page,
}: WrapperProps): React.ReactElement => {
  const loadingComponent = (
    <div style={{ textAlign: 'center' }}>
      <Loader type='circular' size='large' />
    </div>
  );
  const errorComponent = (
    <Alert type='error'>
      <FormattedMessage
        id='cs_perfportal_error_reason_1'
        defaultMessage='Your credentials may be incorrect or inadequate.'
      />
    </Alert>
  );
  const handleCreateGoalOpen = () => {
    if (setIsCreateGoalOpen) {
      setIsCreateGoalOpen(true);
    }
  };

  const globalState = useContext(StateContext);
  const { inspireOptions } = globalState.state;
  const { isTm, isAdmin, userID } = inspireOptions.data;
  const { isProxying } = useProxy();

  // display create goal button if
  // 1. you are a TM and you are creating goal for yourself or your direct reports
  // 2. you are the TM of the displayed CSA
  // 3. you are admin
  // 4. you are not in proxy mode and creating a goal for your own team
  // 5. setIsCreateGoalOpen callback is provided (enables goal creation UI)
  const canCreateGoal =
    setIsCreateGoalOpen &&
    (isAdmin ||
      (isTm &&
        (loggedInProfile.user.alias === displayedProfile.user.alias ||
          loggedInProfile.directReports.some(
            user => user.userID === displayedProfile.user.userID
          )) &&
        !isProxying));

  // lock to display associate goals if the displayed profile has no direct report
  // show view for admin user profiles
  const isAdminSelfProfile = isAdmin && displayedProfile.user.userID === userID;
  const lockToAssociateGoals =
    !isAdminSelfProfile && displayedProfile.directReports.length == 0;

  return (
    <>
      {!loaded ? (
        <Column spacingInset='500 none'>
          <Row width='100%' widths='fill'>
            <Text type='h400'>
              <FormattedMessage
                id='cs_perfportal_goals'
                defaultMessage='Goals'
              />
            </Text>
          </Row>
          {loading && loadingComponent}
          {error && errorComponent}
        </Column>
      ) : (
        <Column spacingInset='500 none'>
          <Row width='100%' widths='fill'>
            {!lockToAssociateGoals ? (
              <Text type='h400'>
                <FormattedMessage
                  id='cs_perfportal_goals'
                  defaultMessage='Goals'
                />
              </Text>
            ) : (
              <Text type='h400'>
                <span id='nav-goals' />
                <FormattedMessage
                  id='cs_perfportal_goal_display_title'
                  defaultMessage={`${displayedProfile.user.displayName}'s Goals`}
                  values={{
                    displayName: displayedProfile.user.displayName,
                  }}
                />
              </Text>
            )}
            <Row alignmentHorizontal='end'>
              {canCreateGoal && (
                <CreateGoalButton
                  onOpen={handleCreateGoalOpen}
                  username={displayedProfile.user.userName}
                  page={page}
                />
              )}
            </Row>
          </Row>
          <Row>
            <Goals
              profile={displayedProfile}
              cscEntityPrefix={inspireOptions.data.cscEntityPrefix}
              changeFlag={refreshGoalsFlag}
              lockedGoalAssociate={
                lockToAssociateGoals ? displayedProfile.user.userID : undefined
              }
              page={page}
            ></Goals>
          </Row>
        </Column>
      )}
    </>
  );
};

export default GoalSettingContainerWrapper;
